/**
 * DebateDemo - Real-time debate visualization using backend SSE streaming
 *
 * Shows AI models debating a question with:
 * - Real API calls to the research backend
 * - Mediator opening and agent arguments streaming in real-time
 * - Consensus reaching with agreement score
 *
 * NOTE: This demo uses force_debate=true to always trigger debate
 * regardless of confidence score, ensuring users can see the debate feature.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';
import { API_BASE_URL } from '@/lib/api';
import {
  MessageSquare,
  Scale,
  Zap,
  Users,
  X,
  AlertTriangle,
  Loader2,
  Search,
  Shield,
  CheckCircle,
} from 'lucide-react';
import { getProviderFromModel, getProviderLogo, getProviderColor as getProviderColorUtil } from '@/utils/providerUtils';

// Types for SSE data
interface DebateArgument {
  roundNumber: number;
  sequenceInRound: number;
  agentName: string;
  modelName: string;
  argument: string;
}

interface DebateDemoProps {
  question: string;
  onClose?: () => void;
  className?: string;
  /** Real browser fingerprint for showcase limit tracking */
  fingerprintId?: string;
  /** Callback when showcase limit is reached */
  onLimitReached?: () => void;
}

export function DebateDemo({ question, onClose, className, fingerprintId, onLimitReached }: DebateDemoProps) {
  // Phases: connecting -> researching -> fact-checking -> debating -> complete | error
  const [phase, setPhase] = useState<'connecting' | 'researching' | 'fact-checking' | 'debating' | 'complete' | 'error'>('connecting');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Mediator state
  const [mediatorOpening, setMediatorOpening] = useState<string>('');
  const [mediatorModel, setMediatorModel] = useState<string>('');
  const [discrepancies, setDiscrepancies] = useState<string[]>([]);

  // Debate arguments
  const [arguments_, setArguments] = useState<DebateArgument[]>([]);
  const [currentRound, setCurrentRound] = useState<number>(0);
  const [maxRounds, setMaxRounds] = useState<number>(2);

  // Consensus
  const [consensus, setConsensus] = useState<string>('');
  const [agreementScore, setAgreementScore] = useState<number>(0);
  const [consensusReached, setConsensusReached] = useState<boolean>(false);

  // Progress tracking (for phases before debate)
  const [researchProgress, setResearchProgress] = useState<string>('');

  // Track whether debate occurred and fact-check confidence
  const [hadDebate, setHadDebate] = useState<boolean>(false);

  // Convergence trajectory: accumulated from per-round agreement scores
  const [convergenceScores, setConvergenceScores] = useState<number[]>([]);
  const [factCheckConfidence, setFactCheckConfidence] = useState<number>(0);

  const scrollRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Auto-scroll as new messages appear
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [arguments_, mediatorOpening, consensus]);

  // Start research on mount
  const startResearch = useCallback(async () => {
    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Reset state
    setPhase('connecting');
    setErrorMessage('');
    setMediatorOpening('');
    setMediatorModel('');
    setDiscrepancies([]);
    setArguments([]);
    setCurrentRound(0);
    setConsensus('');
    setAgreementScore(0);
    setConsensusReached(false);
    setResearchProgress('');
    setHadDebate(false);
    setFactCheckConfidence(0);

    try {
      // Use real fingerprint for showcase limit tracking, fallback to timestamp-based ID
      const effectiveFingerprint = fingerprintId || `demo_debate_${Date.now()}`;

      const response = await fetch(`${API_BASE_URL}/api/v1/free-report/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic: question,
          turnstile_token: 'XXXX.DEMO.TOKEN.XXXX',
          tasks: [{ name: 'Research', description: question, execution_mode: 'parallel' }],
          fingerprint_id: effectiveFingerprint,
          force_debate: true,  // Always trigger debate for showcase demo
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i];
          if (line.startsWith('event: ')) {
            // Handle "event: type\ndata: json" format
            const eventType = line.slice(7).trim();
            const dataLine = lines[i + 1];
            if (dataLine?.startsWith('data: ')) {
              try {
                const data = JSON.parse(dataLine.slice(6));
                handleSSEEvent({ event_type: eventType, data });
                i++; // Skip the data line since we processed it
              } catch {
                // Skip invalid JSON
              }
            }
          } else if (line.startsWith('data: ')) {
            // Handle data-only format with event_type inside JSON
            try {
              const eventData = JSON.parse(line.slice(6));
              if (eventData.event_type) {
                handleSSEEvent(eventData);
              } else if (eventData.event) {
                // Alternative format where event type is 'event' not 'event_type'
                handleSSEEvent({ event_type: eventData.event, data: eventData });
              }
            } catch {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      setPhase('error');
      setErrorMessage((err as Error).message || 'Connection failed');
    }
  }, [question]);

  // Handle SSE events
  const handleSSEEvent = useCallback((event: { event_type: string; data: Record<string, unknown> }) => {
    const { event_type, data } = event;

    switch (event_type) {
      case 'stage':
        // Update phase based on stage
        if (data.stage === 'researching') {
          setPhase('researching');
          setResearchProgress('Research agents starting...');
        } else if (data.stage === 'fact_checking') {
          setPhase('fact-checking');
          setResearchProgress('Fact-checker validating claims...');
        }
        break;

      case 'agent_start':
        setResearchProgress(`Agent ${data.agent_name} researching...`);
        break;

      case 'agent_complete':
        setResearchProgress(`Agent ${data.agent_name} complete`);
        break;

      case 'fact_check_start':
        setPhase('fact-checking');
        setResearchProgress('Fact-checker analyzing findings...');
        break;

      case 'fact_check_complete': {
        const confidence = data.confidence_score as number;
        setFactCheckConfidence(confidence);
        setResearchProgress(`Fact-check done: ${(confidence * 100).toFixed(0)}% confidence`);
        break;
      }

      case 'debate_start':
        setPhase('debating');
        setHadDebate(true);
        setMaxRounds(data.max_rounds as number || 2);
        setDiscrepancies(data.discrepancies as string[] || []);
        break;

      case 'debate_mediator_open':
        setMediatorOpening(data.opening_statement as string || '');
        setMediatorModel(data.mediator_model as string || '');
        setDiscrepancies(data.discrepancies as string[] || []);
        break;

      case 'debate_round_start':
        setCurrentRound(data.round_number as number);
        break;

      case 'debate_agent_argument':
        setArguments(prev => [...prev, {
          roundNumber: data.round_number as number,
          sequenceInRound: data.sequence_in_round as number,
          agentName: data.agent_name as string,
          modelName: data.model_name as string,
          argument: data.argument as string,
        }]);
        break;

      case 'debate_round_complete':
        setAgreementScore(data.agreement_score as number || 0);
        setConvergenceScores(prev => [...prev, data.agreement_score as number || 0]);
        break;

      case 'debate_mediator_close':
        setConsensus(data.final_consensus as string || '');
        setAgreementScore(data.agreement_score as number || 0);
        setConsensusReached(data.consensus_reached as boolean || false);
        break;

      case 'debate_complete':
        setPhase('complete');
        setAgreementScore(data.agreement_score as number || 0);
        setConsensusReached(data.consensus_reached as boolean || false);
        break;

      case 'done':
        // Only set default message if we never had a debate (no consensus from mediator)
        setPhase('complete');
        setConsensus((prevConsensus) => {
          // Keep the mediator's consensus if we had a debate, otherwise show default
          if (prevConsensus) {
            return prevConsensus;
          }
          return 'Research completed without requiring debate - high confidence in findings.';
        });
        break;

      case 'error':
        setPhase('error');
        setErrorMessage(data.error as string || 'An error occurred');
        // Check if showcase demo limit was reached
        if (data.limit_reached && onLimitReached) {
          onLimitReached();
        }
        break;
    }
  }, [phase, onLimitReached]);

  // Start research on mount
  useEffect(() => {
    startResearch();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [startResearch]);

  // Calculate progress for the circular indicator
  const totalSteps = 1 + maxRounds * 3 + 1; // mediator + (rounds * 3 agents avg) + consensus
  const currentSteps = 1 + arguments_.length + (consensus ? 1 : 0);
  const progress = phase === 'complete' ? 100 : Math.min((currentSteps / totalSteps) * 100, 95);

  return (
    <div className={cn(
      'relative flex flex-col h-full rounded-xl border-2 overflow-hidden',
      'backdrop-blur-md bg-gradient-to-br from-blue-500/10 to-indigo-500/5',
      'border-blue-500/30 shadow-xl',
      className
    )}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border-subtle">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-5 h-5 text-blue-400" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-text-primary">AI Council Debate</h3>
            <p className="text-xs text-text-secondary">"{question}"</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Progress indicator */}
          <div className="relative w-8 h-8">
            <svg className="w-8 h-8 -rotate-90" viewBox="0 0 32 32">
              <circle cx="16" cy="16" r="12" fill="none" stroke="currentColor" strokeWidth="2" className="text-text-muted" />
              <circle
                cx="16" cy="16" r="12" fill="none" stroke="currentColor" strokeWidth="2"
                className={phase === 'complete' ? "text-verdict-primary" : "text-blue-400"}
                strokeDasharray="75.4"
                strokeDashoffset={75.4 * (1 - progress / 100)}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 0.5s ease-out' }}
              />
            </svg>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-surface-light transition-colors"
            >
              <X className="w-4 h-4 text-text-secondary" />
            </button>
          )}
        </div>
      </div>

      {/* Debate content - scrollable */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* Connecting state */}
        {phase === 'connecting' && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-blue-400 animate-spin mr-3" />
            <span className="text-sm text-text-secondary">Connecting to research agents...</span>
          </div>
        )}

        {/* Error state */}
        {phase === 'error' && (
          <div className="flex flex-col items-center justify-center py-8 gap-3">
            <AlertTriangle className="w-8 h-8 text-red-400" />
            <span className="text-sm text-red-400">{errorMessage || 'Connection failed'}</span>
            <button
              onClick={startResearch}
              className="px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400 text-sm hover:bg-blue-500/30 transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {/* Pre-debate phases (researching, fact-checking) */}
        {(phase === 'researching' || phase === 'fact-checking') && (
          <div className="space-y-3">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-surface-light border border-border-subtle">
              {phase === 'researching' ? (
                <Search className="w-4 h-4 text-emerald-400 animate-pulse" />
              ) : (
                <Shield className="w-4 h-4 text-yellow-400 animate-pulse" />
              )}
              <span className="text-sm text-text-primary">{researchProgress}</span>
            </div>
            <p className="text-xs text-text-muted text-center">
              Debate will begin if fact-checker finds discrepancies or low confidence...
            </p>
          </div>
        )}

        {/* Debating phase content */}
        {(phase === 'debating' || phase === 'complete') && (
          <>
            {/* Discrepancies that triggered debate */}
            {discrepancies.length > 0 && (
              <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 mb-2">
                <div className="flex items-center gap-2 text-xs text-amber-400 font-mono mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="font-medium">Discrepancies to Resolve:</span>
                </div>
                <ul className="space-y-1">
                  {discrepancies.map((d, idx) => (
                    <li key={idx} className="text-xs text-text-primary pl-3 border-l-2 border-amber-500/40">
                      {d}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Mediator opening */}
            {mediatorOpening && (
              <div className="animate-fadeIn p-3 rounded-lg backdrop-blur-sm bg-gradient-to-r from-cyan-500/15 to-cyan-600/5 border border-cyan-500/30">
                <div className="flex items-center gap-2 text-xs text-cyan-400 font-mono mb-1.5">
                  <Users className="w-4 h-4" />
                  <span className="font-medium">Moderator</span>
                  {mediatorModel && <span className="text-text-muted">({mediatorModel})</span>}
                </div>
                <div className="text-sm text-text-primary leading-relaxed prose prose-invert prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-strong:text-text-primary">
                  <ReactMarkdown>{mediatorOpening}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Debate arguments — grouped by round */}
            {(() => {
              const roundGroups = new Map<number, DebateArgument[]>();
              for (const arg of arguments_) {
                const r = arg.roundNumber;
                if (!roundGroups.has(r)) roundGroups.set(r, []);
                roundGroups.get(r)!.push(arg);
              }
              const sortedRounds = Array.from(roundGroups.entries()).sort(([a], [b]) => a - b);
              return sortedRounds.map(([roundNum, args], groupIdx) => (
                <div key={roundNum}>
                  {sortedRounds.length > 1 && (
                    <div className="flex items-center gap-2 my-2">
                      <div className="h-px flex-1 bg-surface-raised/60" />
                      <span className="text-[9px] font-mono text-text-muted uppercase tracking-wider">
                        {groupIdx === 0 ? 'Initial Positions' : `Round ${roundNum} — Debate`}
                      </span>
                      <div className="h-px flex-1 bg-surface-raised/60" />
                    </div>
                  )}
                  {args.map((arg, idx) => {
                    const isLeft = idx % 2 === 0;
                    const provider = getProviderFromModel(arg.modelName);
                    return (
                      <div
                        key={`${roundNum}-${idx}`}
                        className={cn(
                          'animate-fadeIn flex',
                          isLeft ? 'justify-start' : 'justify-end'
                        )}
                      >
                        <div className={cn(
                          'max-w-[85%] p-3 rounded-xl border transition-all duration-300',
                          'backdrop-blur-sm bg-gradient-to-br',
                          getProviderColorUtil(provider, 'gradient'),
                          isLeft ? 'rounded-tl-sm' : 'rounded-tr-sm'
                        )}>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <div className={cn(
                              'w-6 h-6 rounded-full flex items-center justify-center bg-surface-light'
                            )}>
                              <span className="flex items-center justify-center">{getProviderLogo(provider, 16)}</span>
                            </div>
                            <span className={cn('text-xs font-mono font-medium', getProviderColorUtil(provider, 'text'))}>
                              {arg.agentName}
                            </span>
                            <span className="text-[10px] text-text-muted ml-auto">
                              R{arg.roundNumber}
                            </span>
                          </div>
                          <div className="text-xs text-text-primary leading-relaxed prose prose-invert prose-xs max-w-none prose-p:my-0.5 prose-ul:my-0.5 prose-li:my-0 prose-strong:text-text-primary">
                            <ReactMarkdown>{arg.argument}</ReactMarkdown>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ));
            })()}

            {/* Consensus / Completion message */}
            {consensus && (
              <div className={cn(
                "animate-fadeIn relative p-4 rounded-lg backdrop-blur-sm border",
                hadDebate
                  ? "bg-gradient-to-r from-verdict-primary/20 to-amber-500/10 border-verdict-primary/40"
                  : "bg-gradient-to-r from-emerald-500/20 to-green-500/10 border-emerald-500/40"
              )}>
                <div className={cn(
                  "absolute top-0 left-4 right-4 h-0.5 bg-gradient-to-r from-transparent to-transparent",
                  hadDebate ? "via-verdict-primary" : "via-emerald-500"
                )} />
                <div className={cn(
                  "flex items-center gap-2 text-xs font-mono mb-2 pt-1",
                  hadDebate ? "text-verdict-primary" : "text-emerald-400"
                )}>
                  {hadDebate ? (
                    <Scale className="w-4 h-4" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  <span className="font-bold uppercase tracking-wider">
                    {hadDebate
                      ? (consensusReached ? 'Consensus Reached' : 'Final Assessment')
                      : 'Research Complete'}
                  </span>
                  {/* Show score: agreement score for debates, fact-check confidence for no-debate */}
                  <span className={cn(
                    'ml-auto px-2 py-0.5 rounded-full font-bold',
                    hadDebate
                      ? (agreementScore >= 0.7
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : agreementScore >= 0.5
                            ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-red-500/20 text-red-400')
                      : (factCheckConfidence >= 0.7
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-amber-500/20 text-amber-400')
                  )}>
                    {hadDebate
                      ? `${(agreementScore * 100).toFixed(0)}%`
                      : `${(factCheckConfidence * 100).toFixed(0)}%`}
                  </span>
                </div>
                <div className="text-sm text-text-primary leading-relaxed prose prose-invert prose-sm max-w-none prose-p:my-1 prose-ul:my-1 prose-strong:text-text-primary">
                  <ReactMarkdown>{consensus}</ReactMarkdown>
                </div>

                {/* Convergence Sparkline */}
                {hadDebate && convergenceScores.length >= 2 && (
                  <div className="mt-3 border-t border-border-subtle pt-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-text-muted">Convergence Trajectory</span>
                      <span className="text-text-secondary font-mono text-[10px]">
                        {(convergenceScores[0] * 100).toFixed(0)}% → {(convergenceScores[convergenceScores.length - 1] * 100).toFixed(0)}%
                      </span>
                    </div>
                    <svg width="100%" height="24" viewBox="0 0 200 24" preserveAspectRatio="none" className="rounded">
                      <line x1="2" y1="12" x2="198" y2="12" stroke="rgb(var(--color-border))" strokeWidth={0.5} strokeDasharray="4 2" />
                      <polyline
                        fill="none"
                        stroke={convergenceScores[convergenceScores.length - 1] > convergenceScores[0] + 0.05 ? '#10b981' : '#f59e0b'}
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        points={convergenceScores.map((v, i) => `${2 + (i / (convergenceScores.length - 1)) * 196},${2 + (1 - v) * 20}`).join(' ')}
                      />
                    </svg>
                  </div>
                )}

                {/* Echo Chamber Warning */}
                {hadDebate && convergenceScores.length > 0 && convergenceScores[0] > 0.8 && (convergenceScores[convergenceScores.length - 1] - convergenceScores[0]) < 0.05 && (
                  <div className="mt-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                    <span className="text-xs text-amber-300">
                      Echo chamber risk — agents showed high initial agreement. Confidence has been adjusted.
                    </span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Status bar */}
      <div className="p-3 border-t border-border-subtle bg-bar-bg/30">
        <div className="flex items-center gap-2">
          {phase === 'connecting' && (
            <>
              <Loader2 className="w-4 h-4 text-text-secondary animate-spin" />
              <span className="text-xs text-text-secondary">Connecting...</span>
            </>
          )}
          {phase === 'researching' && (
            <>
              <Search className="w-4 h-4 text-emerald-400 animate-pulse" />
              <span className="text-xs text-text-secondary">Research in progress...</span>
            </>
          )}
          {phase === 'fact-checking' && (
            <>
              <Shield className="w-4 h-4 text-yellow-400 animate-pulse" />
              <span className="text-xs text-text-secondary">Fact-checking findings...</span>
            </>
          )}
          {phase === 'debating' && (
            <>
              <Zap className="w-4 h-4 text-blue-400 animate-pulse" />
              <span className="text-xs text-text-secondary">
                Debate in progress - Round {currentRound} of {maxRounds}
              </span>
            </>
          )}
          {phase === 'complete' && (
            <>
              <Scale className="w-4 h-4 text-verdict-primary" />
              <span className="text-xs text-verdict-primary">
                {arguments_.length > 0 ? 'Council has reached consensus' : 'Research complete - High confidence'}
              </span>
            </>
          )}
          {phase === 'error' && (
            <>
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-xs text-error">Error occurred</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

